import { Provider } from "@redotech/redo-order-common/provider";
import { privacy } from "@redotech/schema-privacy";
import { FulfillmentHoldReason } from "@redotech/shopify-client/admin.graphql";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "./date-schema";
import { RedoShopifyFulfillmentHoldReason } from "./fulfillment-hold-reason";

const FulfillmentHoldSchema_ = z
  .object({
    holdHandle: z.string().meta({ privacy: privacy.nonPii }),
    provider: createEnum(Provider, { enumName: "Provider" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    reason: createEnum(RedoShopifyFulfillmentHoldReason, {
      enumName: "RedoShopifyFulfillmentHoldReason",
    }).meta({ privacy: privacy.nonPii }),
    reasonNotes: z.string().meta({ privacy: privacy.free_text }),
    placedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export const FulfillmentHoldSchema: z.ZodType<FulfillmentHold> =
  FulfillmentHoldSchema_;

export interface FulfillmentHold extends z.infer<
  typeof FulfillmentHoldSchema_
> {}

/**
 * Maps our internal FulfillmentHoldReasons to Shopify's hold reasons
 */
export const FULFILLMENT_HOLD_REASON_MAP: {
  [key in RedoShopifyFulfillmentHoldReason]: FulfillmentHoldReason;
} = {
  [RedoShopifyFulfillmentHoldReason.InventoryOutOfStock]:
    FulfillmentHoldReason.InventoryOutOfStock,
  [RedoShopifyFulfillmentHoldReason.IncorrectAddress]:
    FulfillmentHoldReason.IncorrectAddress,
  [RedoShopifyFulfillmentHoldReason.AwaitingPayment]:
    FulfillmentHoldReason.AwaitingPayment,
  [RedoShopifyFulfillmentHoldReason.HighRiskOfFraud]:
    FulfillmentHoldReason.HighRiskOfFraud,
  [RedoShopifyFulfillmentHoldReason.Other]: FulfillmentHoldReason.Other,
  [RedoShopifyFulfillmentHoldReason.AwaitingReturnItems]:
    FulfillmentHoldReason.AwaitingReturnItems,
  [RedoShopifyFulfillmentHoldReason.OnlineStorePostPurchaseCrossSell]:
    FulfillmentHoldReason.OnlineStorePostPurchaseCrossSell,
  [RedoShopifyFulfillmentHoldReason.UnknownDeliveryDate]:
    FulfillmentHoldReason.UnknownDeliveryDate,
};

/**
 * Maps Shopify's hold reasons to our internal FulfillmentHoldReasons
 */
export const FULFILLMENT_HOLD_REASON_MAP_REVERSE: {
  [key in FulfillmentHoldReason]: RedoShopifyFulfillmentHoldReason;
} = {
  [FulfillmentHoldReason.InventoryOutOfStock]:
    RedoShopifyFulfillmentHoldReason.InventoryOutOfStock,
  [FulfillmentHoldReason.IncorrectAddress]:
    RedoShopifyFulfillmentHoldReason.IncorrectAddress,
  [FulfillmentHoldReason.AwaitingPayment]:
    RedoShopifyFulfillmentHoldReason.AwaitingPayment,
  [FulfillmentHoldReason.HighRiskOfFraud]:
    RedoShopifyFulfillmentHoldReason.HighRiskOfFraud,
  [FulfillmentHoldReason.Other]: RedoShopifyFulfillmentHoldReason.Other,
  [FulfillmentHoldReason.AwaitingReturnItems]:
    RedoShopifyFulfillmentHoldReason.AwaitingReturnItems,
  [FulfillmentHoldReason.OnlineStorePostPurchaseCrossSell]:
    RedoShopifyFulfillmentHoldReason.OnlineStorePostPurchaseCrossSell,
  [FulfillmentHoldReason.UnknownDeliveryDate]:
    RedoShopifyFulfillmentHoldReason.UnknownDeliveryDate,
};
